import { useState, useMemo } from "react";
import { useTheme } from "../../../contexts/ThemeContext";
import { useFlipStorage } from "../../../contexts/FlipStorageContext";

function getLast7DayLabels(): string[] {
  const days: string[] = [];
  for (let i = 6; i >= 0; i--) {
    const d = new Date();
    d.setDate(d.getDate() - i);
    days.push(d.toLocaleDateString("en-US", { month: "short", day: "numeric" }));
  }
  return days;
}

export default function PerformanceStats() {
  const { isDark } = useTheme();
  const { entries } = useFlipStorage();
  const [hoveredBar, setHoveredBar] = useState<number | null>(null);

  const sold = useMemo(() => entries.filter((e) => e.status === "sold"), [entries]);

  const totalProfit = useMemo(() => sold.reduce((sum, e) => sum + e.profit, 0), [sold]);
  const totalSales = sold.length;
  const avgROI = useMemo(() => {
    if (sold.length === 0) return 0;
    const sum = sold.reduce((acc, e) => acc + (e.buyPrice > 0 ? (e.profit / e.buyPrice) * 100 : 0), 0);
    return sum / sold.length;
  }, [sold]);

  const bestDayData = useMemo(() => {
    if (sold.length === 0) return { date: "—", profit: 0 };
    const byDate: Record<string, number> = {};
    sold.forEach((e) => { byDate[e.date] = (byDate[e.date] ?? 0) + e.profit; });
    const best = Object.entries(byDate).sort((a, b) => b[1] - a[1])[0];
    return best ? { date: best[0], profit: best[1] } : { date: "—", profit: 0 };
  }, [sold]);

  const dayLabels = useMemo(() => getLast7DayLabels(), []);

  const dailyData = useMemo(() => {
    return dayLabels.map((label) => {
      const dayEntries = sold.filter((e) => e.date === label);
      return {
        date: label,
        profit: dayEntries.reduce((sum, e) => sum + e.profit, 0),
        sales: dayEntries.length,
        cost: dayEntries.reduce((sum, e) => sum + e.buyPrice, 0),
        revenue: dayEntries.reduce((sum, e) => sum + e.sellPrice, 0),
      };
    });
  }, [sold, dayLabels]);

  const maxProfit = Math.max(...dailyData.map((d) => d.profit), 1);

  const hasData = entries.length > 0;

  const STAT_CARDS = [
    { label: "Total Profit", value: `$${totalProfit.toLocaleString()}`, icon: "ri-money-dollar-circle-line", sub: "All time (sold)", trend: totalSales > 0 ? `${totalSales} sales` : "—" },
    { label: "Total Sales", value: totalSales.toString(), icon: "ri-shopping-bag-3-line", sub: "Items sold", trend: totalSales > 0 ? `+${totalSales}` : "—" },
    { label: "Avg. ROI", value: avgROI > 0 ? `${avgROI.toFixed(1)}%` : "—", icon: "ri-line-chart-line", sub: "Return on investment", trend: avgROI > 0 ? `${avgROI.toFixed(1)}%` : "—" },
    { label: "Best Day", value: bestDayData.date, icon: "ri-trophy-line", sub: "Highest profit", trend: bestDayData.profit > 0 ? `$${bestDayData.profit}` : "—" },
  ];

  return (
    <div>
      {/* Stat Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        {STAT_CARDS.map((card) => (
          <div
            key={card.label}
            className={`rounded-2xl p-5 border ${isDark ? "bg-[#0d0d0d] border-white/6" : "bg-[#f8f8f8] border-black/6"}`}
          >
            <div className="flex items-start justify-between mb-3">
              <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${isDark ? "bg-white/6" : "bg-black/6"}`}>
                <i className={`${card.icon} text-sm ${isDark ? "text-white/50" : "text-[#666]"}`} />
              </div>
              <span className={`font-inter text-[10px] font-medium px-2 py-0.5 rounded-full ${isDark ? "bg-white/5 text-[#555]" : "bg-black/5 text-[#aaa]"}`}>
                {card.trend}
              </span>
            </div>
            <p className={`font-syne font-bold text-2xl mb-0.5 ${isDark ? "text-white" : "text-[#111]"}`}>
              {card.value}
            </p>
            <p className={`font-inter text-xs ${isDark ? "text-[#555]" : "text-[#999]"}`}>
              {card.sub}
            </p>
          </div>
        ))}
      </div>

      {/* Bar Chart */}
      <div className={`rounded-2xl p-6 border ${isDark ? "bg-[#0d0d0d] border-white/6" : "bg-[#f8f8f8] border-black/6"}`}>
        <div className="flex items-center justify-between mb-6">
          <div>
            <h3 className={`font-syne font-semibold text-base ${isDark ? "text-white" : "text-[#111]"}`}>
              Daily Profit
            </h3>
            <p className={`font-inter text-xs mt-0.5 ${isDark ? "text-[#555]" : "text-[#999]"}`}>
              Last 7 days
            </p>
          </div>
          <span className={`font-inter text-xs px-3 py-1.5 rounded-full border ${isDark ? "border-white/8 text-[#555]" : "border-black/8 text-[#999]"}`}>
            {dayLabels[0]} – {dayLabels[6]}
          </span>
        </div>

        {!hasData ? (
          <div className={`flex flex-col items-center justify-center h-40 gap-2 ${isDark ? "text-[#333]" : "text-[#ccc]"}`}>
            <i className="ri-bar-chart-2-line text-3xl" />
            <p className="font-inter text-sm">Save flips from the calculator to see your chart</p>
          </div>
        ) : (
          <div className="flex items-end gap-2 h-40">
            {dailyData.map((day, i) => {
              const barHeight = Math.round((day.profit / maxProfit) * 100);
              return (
                <div
                  key={day.date}
                  className="flex-1 flex flex-col items-center gap-1 cursor-pointer"
                  onMouseEnter={() => setHoveredBar(i)}
                  onMouseLeave={() => setHoveredBar(null)}
                >
                  {hoveredBar === i ? (
                    <div className={`text-center mb-1 px-2 py-1 rounded-lg text-[10px] font-inter font-medium whitespace-nowrap ${isDark ? "bg-white/10 text-white" : "bg-black/8 text-[#111]"}`}>
                      ${day.profit}
                    </div>
                  ) : (
                    <div className="h-6" />
                  )}
                  <div className="w-full flex items-end" style={{ height: "100px" }}>
                    <div
                      className={`w-full rounded-t-lg transition-all duration-200 ${
                        day.profit === 0
                          ? isDark ? "bg-white/4" : "bg-black/4"
                          : hoveredBar === i
                          ? isDark ? "bg-white/60" : "bg-[#111]/80"
                          : isDark ? "bg-white/15" : "bg-black/12"
                      }`}
                      style={{ height: day.profit === 0 ? "4px" : `${barHeight}%` }}
                    />
                  </div>
                  <p className={`font-inter text-[10px] text-center ${isDark ? "text-[#444]" : "text-[#bbb]"}`}>
                    {day.date.split(" ")[1]}
                  </p>
                </div>
              );
            })}
          </div>
        )}

        <div className={`flex items-center gap-5 mt-4 pt-4 border-t ${isDark ? "border-white/5" : "border-black/5"}`}>
          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <div className={`w-2 h-2 rounded-full ${isDark ? "bg-white/50" : "bg-[#333]"}`} />
              <span className={`font-inter text-xs ${isDark ? "text-[#555]" : "text-[#999]"}`}>Profit</span>
            </div>
            {hasData && (
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-emerald-500/60" />
                <span className={`font-inter text-xs ${isDark ? "text-[#555]" : "text-[#999]"}`}>
                  ${totalSales > 0 ? (totalProfit / totalSales).toFixed(0) : "0"} avg/sale
                </span>
              </div>
            )}
            {!hasData && (
              <span className={`font-inter text-xs ${isDark ? "text-[#333]" : "text-[#ddd]"}`}>
                Data populates as you save flips
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
